import { AssessmentType, SurveyStatus } from '@@types/api.types';
import { RETURN_TO_SEARCH_PARAM, getSafeReturnToPath } from '@hooks/return-to.hook';
import { useGetSurveys } from '@queries/survey.query';
import { surveySidebarStore } from '@stores/survey-sidebar.store';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const SURVEY_PATH_REGEX = /\/survey\/([^/?#]+)/;

const isSurveyPath = (path: string): boolean => {
  return SURVEY_PATH_REGEX.test(path);
};

const stripReturnToParam = (path: string): string => {
  const [pathWithoutSearch, existingSearch = ''] = path.split('?');
  const searchParams = new URLSearchParams(existingSearch);
  searchParams.delete(RETURN_TO_SEARCH_PARAM);
  const nextSearch = searchParams.toString();

  if (!nextSearch) {
    return pathWithoutSearch;
  }

  return `${pathWithoutSearch}?${nextSearch}`;
};

const getSurveyIdFromPath = (path: string): string | null => {
  const match = path.match(SURVEY_PATH_REGEX);
  return match?.[1] ?? null;
};

const isValidOriginPath = (path: string | null): path is string => {
  return Boolean(getSafeReturnToPath({ returnTo: path }));
};

const appendReturnToPath = (params: { origin: string; path: string }): string => {
  const { origin, path } = params;

  if (!isValidOriginPath(origin)) {
    return path;
  }

  const [pathWithoutSearch, existingSearch = ''] = path.split('?');
  const nextSearchParams = new URLSearchParams(existingSearch);
  nextSearchParams.set(RETURN_TO_SEARCH_PARAM, origin);
  const nextSearch = nextSearchParams.toString();

  if (!nextSearch) {
    return pathWithoutSearch;
  }

  return `${pathWithoutSearch}?${nextSearch}`;
};

const getCurrentOriginPath = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  return stripReturnToParam(currentPath);
};

/**
 * Hook that provides a navigation function that resets the survey sidebar state
 * before navigating. Use this whenever navigating to a survey page to ensure
 * the sidebar starts with fresh default state.
 */
export const useSurveyNavigation = (): {
  navigateToSurvey: (path: string) => void;
} => {
  const navigate = useNavigate();

  const navigateToSurvey = useCallback(
    (path: string) => {
      const surveyId = getSurveyIdFromPath(path);
      const origin = getCurrentOriginPath();
      const safeOrigin = isValidOriginPath(origin) ? origin : null;
      const surveyPath =
        surveyId && safeOrigin && !isSurveyPath(safeOrigin) ? appendReturnToPath({ origin: safeOrigin, path }) : path;

      surveySidebarStore.getState().reset();
      navigate(surveyPath);
    },
    [navigate],
  );

  return { navigateToSurvey };
};

export const useDraftSurveyState = (assessmentType: AssessmentType) => {
  const { technologyId } = useParams();
  const { navigateToSurvey } = useSurveyNavigation();
  const { data: draftSurveys = [], isPending: isDraftSurveysPending } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId,
  });

  const draftSurvey = draftSurveys.find(
    (survey) => survey.technology?.id === technologyId && survey.assessmentType === assessmentType,
  );

  const hasDraftAssessment = Boolean(draftSurvey);

  const emptyStateMessages = (() => {
    if (hasDraftAssessment) {
      return {
        message: assessmentType === AssessmentType.TRL ? 'Resume Rapid TRL' : 'Resume MRL Assessment',
        title: 'Assessment History Unavailable',
      };
    }

    return {
      message: assessmentType === AssessmentType.TRL ? 'Start Rapid TRL' : 'Start MRL Assessment',
      title: 'Assessment History Unavailable',
    };
  })();

  const handleResumeSurvey = () => {
    if (!draftSurvey) return;
    navigateToSurvey(`/technology/${technologyId}/survey/${draftSurvey.id}?viewFirstUnansweredQuestion=true`);
  };

  return {
    draftSurvey,
    emptyStateMessages,
    hasDraftAssessment,
    handleResumeSurvey,
    isDraftSurveysPending,
  };
};
